import React from "react";
import AppHeading, { Action } from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import DataTable from "../components/data-table/data-table";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../providers/auth-provider";
import isAdmin from "../functions/is-admin";
import { v4 as uuid } from "uuid";
import axios, { errorHandler, responseHandler } from "../requests/axios";
import { toast } from "react-toastify";
import GenericEditDialog from "./components/dialogs/generic-edit-dialog";
import AttachProviderToChannelDialog from "./components/dialogs/attach-provider-to-channel-dialog";

export default function ChannelPage() {
  const [channel, setChannel] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [editChannelDialog, setEditChannelDialog] = React.useState({
    open: false,
    channel: null,
  });
  const [showAttachProviderDialog, setAttachProviderDialog] =
    React.useState(false);
  const tableRef = React.useRef();
  const providerTableRef = React.useRef();
  const navigate = useNavigate();
  const { channel_id } = useParams();
  const { user } = useAuth();

  const detachProvider = React.useCallback(
    (providerId) => {
      axios
        .post(`api/channels/${channel_id}/providers/${providerId}/detach`)
        .then(
          responseHandler(() => {
            toast.success("Provider unlinked successfully");
            refreshProvidersTable();
          })
        )
        .catch(
          errorHandler(() => {
            toast.error("Something went wrong while contacting the server");
          })
        );
    },
    [channel_id]
  );

  const columns = React.useMemo(
    () => [
      {
        label: "Name",
        key: "organisation_name",
        value: "organisation_name",
        width: 90,
      },
      {
        label: "",
        value: "",
        key: "0",
        width: 10,
        renderValue: (row) => (
          <span className="data-table__action-cell">
            <span
              className="data-table__action-cell__item data-table__icon"
              onClick={() =>
                navigate(`/admin/organisations/${row.organisation_id}`)
              }
            >
              <PlayCircleRoundedIcon />
            </span>
          </span>
        ),
      },
    ],
    [navigate]
  );

  const providerColumns = React.useMemo(
    () => [
      {
        label: "Provider Name",
        key: "provider_name",
        value: "provider_name",
        width: 30,
      },
      {
        label: "Label",
        key: "provider_label",
        value: "provider_label",
        width: 30,
      },
      {
        label: "External Reference",
        key: "external_reference",
        value: "external_reference",
        width: 25,
      },
      {
        label: "",
        value: "",
        key: "0",
        width: 15,
        renderValue: (row) => {
          return (
            <span className="data-table__action-cell">
              <span
                className="data-table__action-cell__item data-table__icon"
                onClick={() => detachProvider(row.provider_id)}
              >
                <LinkOffRoundedIcon />
              </span>
            </span>
          );
        },
      },
    ],
    [detachProvider]
  );

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`api/channels/${channel_id}`)
      .then(
        responseHandler((response) => {
          setChannel(response.data);
        })
      )
      .catch(
        errorHandler(() => {
          toast.error("Error occurred while loading Channel");
        })
      )
      .finally(() => setLoading(false));
  }, [channel_id]);

  // admin-only page
  if (!isAdmin(user)) {
    navigate("/");
    return;
  }

  function openChannelEditDialog(channel) {
    setEditChannelDialog({ open: true, channel });
  }

  function closeChannelEditDialog() {
    setEditChannelDialog({ open: false, channel: null });
  }

  function openAttachProviderDialog() {
    setAttachProviderDialog(true);
  }

  function closeAttachProviderDialog() {
    setAttachProviderDialog(false);
  }

  function refreshProvidersTable() {
    if (providerTableRef.current) {
      providerTableRef.current.refreshList();
    }
  }

  function updateChannel(payload) {
    return axios.put(`api/channels/${channel_id}`, payload);
  }

  return (
    <React.Fragment>
      <AppHeading
        title={loading || !channel ? "Loading..." : channel.organisation_name}
        subtitle={
          loading || !channel
            ? ""
            : "View and Edit Channel using the top right icon"
        }
        rightActions={[
          {
            onClick: () => openChannelEditDialog(channel),
            balloonLabel: "Edit Channel",
            icon: EditRoundedIcon,
          },
        ]}
      />

      <AppBody loading={loading}>
        <div className="channel-page">
          <div className="channel-page__table-header">Organisations</div>
          <div className="channel-page__action-row"></div>
          <DataTable
            url={`api/channels/${channel_id}/organisations`}
            columns={columns}
            ref={tableRef}
          />

          <div className="channel-page__divider" />

          <div className="channel-page__table-header">Providers</div>
          <div className="channel-page__action-row">
            <Action
              action={{
                onClick: openAttachProviderDialog,
                balloonLabel: "Attach Providers",
                icon: AddCircleRoundedIcon,
              }}
            />
          </div>
          <DataTable
            url={`api/channels/${channel_id}/providers`}
            columns={providerColumns}
            ref={providerTableRef}
          />
        </div>
      </AppBody>
      <GenericEditDialog
        data={editChannelDialog.channel}
        onSubmit={updateChannel}
        onSuccess={(data) => setChannel(data)}
        onClose={() => closeChannelEditDialog()}
        isOpen={editChannelDialog.open}
        title="Edit Channel"
        successMessage="Channel updated successfully"
        errorMessage="Error occurred while updating Channel"
        fields={[
          {
            type: "text",
            label: "Display Name",
            name: "label",
            required: true,
          },
          {
            type: "text",
            label: "Name",
            name: "key",
            required: true,
          },
          {
            type: "text",
            label: "External ID",
            name: "external_id",
            required: true,
          },
          {
            type: "select",
            label: "Channel Type",
            name: "channel_type",
            required: true,
            options: [
              { label: "Linear", value: "linear" },
              { label: "AVOD", value: "avod" },
            ],
          },
          {
            type: "text",
            label: "Parent Channel Name (optional)",
            name: "parent_channel_name",
            required: false,
          },
        ]}
        key={`edit-dialog-${uuid()}`}
      />
      <AttachProviderToChannelDialog
        onClose={closeAttachProviderDialog}
        onConfirm={() => refreshProvidersTable()}
        isOpen={showAttachProviderDialog}
        channelId={channel_id}
        key={`attach-provider-dialog-${uuid()}`}
      />
    </React.Fragment>
  );
}
